import { useState } from 'react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { specOf } from '@/domain/catalog';
import type { ComponentInstance } from '@/domain/types';
import { ComponentFields } from './ComponentFields';

export interface ComponentFormModalProps {
  open: boolean;
  component: ComponentInstance | null;
  onClose: () => void;
  onSave: (next: ComponentInstance) => void;
}

/**
 * 部件编辑弹窗：草稿态编辑，确认后才写回项目（向导里是即时写入）。
 *
 * 草稿放在内层组件里，并且**只有打开时才挂载** —— 这样每次打开都是一份全新草稿，
 * 既不需要用 effect 去「同步 props 到 state」（那个 effect 会在输入过程中抢焦点），
 * 也不会把上一次的输入残留到下一次打开。
 */
export function ComponentFormModal({ open, component, onClose, onSave }: ComponentFormModalProps) {
  if (!open || !component) return null;
  return (
    <DraftEditor key={component.id} component={component} onClose={onClose} onSave={onSave} />
  );
}

function DraftEditor({
  component,
  onClose,
  onSave,
}: {
  component: ComponentInstance;
  onClose: () => void;
  onSave: (next: ComponentInstance) => void;
}) {
  const [draft, setDraft] = useState<ComponentInstance>(component);
  const spec = specOf(component.kind);

  return (
    <Modal
      open
      onClose={onClose}
      title={`编辑${spec.label}`}
      description="只填你知道的参数，留空的项不会参与计算。"
      footer={
        <>
          <Button size="lg" variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            size="lg"
            variant="primary"
            onClick={() => {
              onSave(draft);
              onClose();
            }}
          >
            保存
          </Button>
        </>
      }
    >
      <ComponentFields component={draft} onChange={setDraft} />
    </Modal>
  );
}
